<script>
import { computed } from 'vue';

import {
  LpTable,
  LpDropdown,
  LpTag,
  LpTooltip,
  LpCard,
  LpMarkerBtn,
} from '@thehotelsnetwork/lampo';

import {
  EllipsisIcon,
  QuestionCircleIcon,
} from '@thehotelsnetwork/icons/vue/linear';

import EditVisibilityButton from '@/modules/Compsets/components/EditVisibilityButton/EditVisibilityButton.vue';

import mergeCollectionsBy from '@/utils/mergeCollectionsBy';
import { capitalize } from '@/utils/stringHelpers';

import i18n from '@/config/i18n';
import rowSorter from '@/utils/rowSorter';

const { t } = i18n.global;

export default {
  components: {
    LpTable,
    LpDropdown,
    LpTag,
    LpTooltip,
    LpCard,
    LpMarkerBtn,
    EditVisibilityButton,
    EllipsisIcon,
    QuestionCircleIcon,
  },
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    overrideLimits: {
      type: Boolean,
    },
    disableEditVisibility: {
      type: Boolean,
    },
  },
  emits: [
    'editVisibility',
    'editCompset',
    'deleteCompset',
  ],
  setup(props) {
    const defaultColumnConfig = [
      {
        label: t('Name'),
        key: 'name',
        sorter: rowSorter,
      },
      {
        label: t('Type'),
        key: 'type',
        sorter: rowSorter,
      },
      {
        label: t('Status'),
        key: 'status',
      },
      {
        label: t('Visible in'),
        key: 'visibleInText',
      },
      {
        label: '',
        key: 'actions',
      },
    ];

    const availableColumns = computed(() => props.columns
      .reduce((acc, curr) => {
        acc[curr.key] = true;
        return acc;
      }, {}));

    const tableColumns = computed(() => mergeCollectionsBy(
      defaultColumnConfig.filter(({ key }) => availableColumns.value[key]),
      props.columns,
      'key',
    ));

    return {
      tableColumns,
      availableColumns,
      capitalize,
    };
  },
};
</script>

<template>
  <LpTable
    :columns="tableColumns"
    :data="data"
    class="compsets-table-wrapper"
  >
    <template #columnHead="props">
      <div class="flex gap-2 items-center">
        <span class="block font-bold">
          {{ props.data.label }}
        </span>

        <LpTooltip
          v-if="props.data.tooltipText"
          size="small"
          mode="hover"
          :max-width="400"
          :min-width="400"
          placement="top"
        >
          <template #trigger>
            <QuestionCircleIcon class="w-5 h-5" />
          </template>
          <template
            #tooltip
          >
            {{ $t(props.data.tooltipText) }}
          </template>
        </LpTooltip>
      </div>
    </template>

    <template
      v-if="'name' in availableColumns"
      #name="props"
    >
      <div
        class="flex items-center"
        data-testid="nameRow"
      >
        <span
          class="inline-block mr-3 w-4 h-4 rounded"
          :style="{ backgroundColor: props.data.color, minWidth: '1rem' }"
        />
        <div class="p-2 text-sm font-normal text-bm-gray-900">
          {{ props.data.name }}
        </div>
      </div>
    </template>

    <template
      v-if="'type' in availableColumns"
      #type="props"
    >
      <div
        class="text-sm font-normal text-bm-gray-900"
        data-testid="typeRow"
      >
        {{ $t(capitalize(props.data.type)) }}
      </div>
    </template>

    <template
      v-if="'status' in availableColumns"
      #status="props"
    >
      <div
        class="flex items-center space-x-2"
        data-testid="statusRow"
      >
        <LpTag
          size="big"
          :color="props.data.statusColor"
        >
          <div class="text-xs font-normal text-bm-gray-900">
            {{ $t(props.data.statusText) }}
          </div>
        </LpTag>

        <LpTag
          v-if="props.data.ready === false"
          size="big"
          color="primary"
        >
          <div class="text-xs font-normal text-bm-gray-900">
            {{ $t('Processing') }}
          </div>
        </LpTag>

        <LpTooltip
          v-if="props.data.statusTooltipText"
          size="small"
          mode="hover"
          :max-width="400"
          :min-width="400"
          placement="top"
          :custom-wrapper="true"
        >
          <template #trigger>
            <QuestionCircleIcon class="w-5 h-5" />
          </template>
          <template
            #tooltip
          >
            <LpCard class="p-5 text-sm">
              <p>
                {{ $t(props.data.statusTooltipText) }}
              </p>
            </LpCard>
          </template>
        </LpTooltip>
      </div>
    </template>

    <template
      v-if="'visibleInText' in availableColumns"
      #visibleInText="props"
    >
      <div
        data-testid="visibleInTextRow"
      >
        <EditVisibilityButton
          :disabled="props.data.type === 'brand'"
          @click="$emit('editVisibility', props.data.id)"
        >
          {{ props.data.visibleInText }}
        </EditVisibilityButton>
      </div>
    </template>

    <template
      v-if="'actions' in availableColumns"
      #actions="props"
    >
      <div
        data-testid="actionsRow"
      >
        <LpDropdown
          x-position="right"
        >
          <template #btn>
            <div class="flex justify-end">
              <EllipsisIcon class="w-5 h-5 cursor-pointer" />
            </div>
          </template>
          <template #menu="{ closeMenu }">
            <LpCard
              rounded-size="big"
              class="flex flex-col gap-2 p-2 w-32 text-sm"
            >
              <LpMarkerBtn
                @click.prevent="() => {
                  $emit('editCompset', { id: props.data.id, type: props.data.type });
                  closeMenu();
                }"
              >
                {{ $t('Edit Compset') }}
              </LpMarkerBtn>
              <LpMarkerBtn
                v-if="!disableEditVisibility && props.data.type !== 'brand'"
                @click.prevent="() => {
                  $emit('editVisibility', props.data.id);
                  closeMenu();
                }"
              >
                {{ $t('Edit visibility') }}
              </LpMarkerBtn>
              <LpMarkerBtn
                v-if="overrideLimits || props.data.canEdit"
                color="danger"
                @click.prevent="() => {
                  $emit('deleteCompset', props.data.id);
                  closeMenu();
                }"
              >
                {{ $t('Delete') }}
              </LpMarkerBtn>
            </LpCard>
          </template>
        </LpDropdown>
      </div>
    </template>
  </LpTable>
</template>

<style lang="postcss" scoped>
.compsets-table-wrapper {
  :deep(> div), :deep(table) {
    overflow: visible;
  }
}
:deep(.lp-table-container) {
  overflow: visible;
  .lp-table {
    overflow: visible;
  }
}
</style>
